import { z } from 'zod'
import type { ApiClient } from './client'

/**
 * Search — the async query job.
 *
 * Unlike every other module here, a search is not one request. It is four:
 *
 *     POST /{clusterId}/wrk/api/job/realtimecost           what will this scan?
 *     POST /{clusterId}/wrk/api/job/invoke                 -> task id
 *     GET  /{clusterId}/wrk/api/dispatcher/task/state/{id} poll until terminal
 *     GET  /{clusterId}/wrk/api/dispatcher/task/result/{id}?pagesize=&pageno=
 *     POST /{clusterId}/wrk/api/job/revoke                 cancel / on unmount
 *
 * The result does not come back from the call that started it, and an abandoned
 * query keeps running server-side until revoked. That is the whole reason this
 * module exposes five small calls rather than one `search()`: the polling loop
 * and the revoke belong to the caller's lifecycle, not to a promise.
 *
 * Shape VERIFIED 2026-08-21 against the gotham scope of ap1 — the full capture,
 * including the payloads quoted below, is docs/inventory/search-capture.md.
 *
 * Facts the capture established that the legacy client does not show:
 *
 * - `scope_id` is the scope SLUG ('gotham'), not a uuid. `clusterId` is a path
 *   segment. Both are required on every call.
 * - EVERY query error is returned by `invoke`, synchronously, as HTTP 200 with
 *   `status: 'error'` — a fifth envelope value beyond the success|failed|invalid
 *   the docs describe. Stream names are validated there too ("Unknown stream:
 *   X"), so a bad query never becomes a task at all. See SearchQueryError.
 * - `parser_error_info.position` exists but was {0,0} for an error at character
 *   31, so it is captured and deliberately never used for caret placement.
 * - Paging re-reads the stored result by task id; it does NOT re-run the query.
 * - Field names are OPAQUE. The same pipe query returns lowercase `cnamtime` on
 *   the SIGNALS stream and PascalCase `CNAMTime` on FIREWALL. Never construct,
 *   normalise or match a field name exactly — take them from `fields_list`.
 */

/* ------------------------------------------------------------------ schemas */

const invokedTask = z.object({
  id: z.string(),
  /** 'PENDING' at the moment of invoke. */
  status: z.string().default(''),
})

/**
 * `data` is an ARRAY and the dql path takes `data[0]`. The id carries an
 * `-id-0` suffix — the workbook block index, always 0 for a single query.
 * (The legacy connectedsignals path reads `data[1]`; that is not this.)
 */
export const invokeResponse = z
  .object({
    status: z.string().default(''),
    message: z.string().nullable().optional(),
    parser_error_info: z
      .object({
        error_msg: z.string().nullable().default(null),
        parsed_query: z.string().nullable().default(null),
        query_with_error: z.string().nullable().default(null),
      })
      .nullable()
      .optional(),
    data: z.array(invokedTask).default([]),
  })
  .passthrough()

export const taskStateResponse = z
  .object({
    status: z.string().default(''),
    /** PENDING | STARTED | SUCCESS | FAILURE | REVOKED. */
    task_state: z.string().nullable().default(null),
    /** 'EXECUTED' on success, null after a revoke. Not in the legacy client. */
    task_stage: z.string().nullable().default(null),
  })
  .passthrough()

/**
 * A result row. Keys are exactly the entries of `fields_list`, values are
 * `string | number | boolean | null` — passthrough because the key set is
 * per-stream and runs to 783 columns on `stream=*`.
 */
export const searchRow = z.record(z.unknown())

export const searchResultResponse = z
  .object({
    status: z.string().default(''),
    task_status: z.string().nullable().default(null),
    /** On an SQL failure this carries the (vague) reason. Absent on success. */
    message: z.string().nullable().optional(),
    total_count: z.number().default(0),
    /** A formatted string, e.g. '0.2184 sec' — not a number. */
    execution_time: z.string().nullable().default(null),
    source_stream: z.array(z.string()).default([]),
    apply_hidden: z.boolean().default(false),
    fields_list: z.array(z.string()).default([]),
    result: z.array(searchRow).default([]),
  })
  .passthrough()

export const costResponse = z
  .object({
    status: z.string().default(''),
    message: z.string().nullable().optional(),
    /** Events the query would scan. */
    evt_len: z.number().nullable().default(null),
  })
  .passthrough()

/** `{ status, result: { STREAM: ["$Field", ...] } }` — captured live. */
export const vfieldsResponse = z
  .object({
    status: z.string().default(''),
    result: z.record(z.array(z.string())).default({}),
  })
  .passthrough()

export type SearchRow = z.output<typeof searchRow>

export interface SearchResultPage {
  rows: SearchRow[]
  /** Every column the query can return, in server order. Often ~90% empty. */
  fields: string[]
  /** Total across all pages of this task's stored result. */
  total: number
  /** e.g. '0.2184 sec'. */
  executionTime: string | null
  /** Which streams actually answered — `stream=*` fans out across all of them. */
  sourceStreams: string[]
  /**
   * True when the task ran but the QUERY failed. This exists because of SQL:
   * a broken SQL statement is accepted at invoke, becomes a task, and that
   * task reaches `task_state: SUCCESS` — the only trace of the failure is
   * `task_status: "Task Execution Failed"` in the result body (captured live;
   * DQL, by contrast, rejects synchronously at invoke). Rendering "0 rows"
   * for such a page would present a failure as an empty result.
   */
  failed: boolean
  /** The server's reason when `failed` — vague ("Something went wrong!") but real. */
  message: string | null
}

export type TaskState = z.output<typeof taskStateResponse>

/* ------------------------------------------------------------------- errors */

/**
 * A query the engine refused: a parse error, or a stream that does not exist.
 *
 * This is the COMMON path, not an exceptional one — anyone typing DQL hits it
 * constantly — so it is a distinct type the UI can render inline next to the
 * query box, rather than an ApiError that reads like the server fell over.
 * `message` is already human-readable ("Invalid LIMIT Clause, found 't'",
 * "Unknown stream: NOSUCHSTREAM"); show it verbatim.
 */
export class SearchQueryError extends Error {
  /** The query as the parser read it. Differs from the input only rarely. */
  readonly parsedQuery: string | null
  readonly body: unknown
  constructor(message: string, parsedQuery: string | null, body: unknown) {
    super(message)
    this.name = 'SearchQueryError'
    this.parsedQuery = parsedQuery
    this.body = body
  }
}

/* ------------------------------------------------------------------ dialect */

export type Dialect = 'dql' | 'sql'

/**
 * Which engine a query is for, decided from its first word.
 *
 * Both dialects run through the same job pipeline, distinguished only by
 * `job_type`. SQL statements can only start with SELECT or WITH; nothing in
 * DQL does (its forms open with `stream=`, `sourcename=`, or a `_fetch`
 * directive). That makes the first word a complete discriminator — no toggle
 * for the user to forget, and the detected dialect is displayed so the one
 * surprising case (a typo'd first word read as DQL) is visible before Run.
 */
export function detectDialect(query: string): Dialect {
  return /^\s*(select|with)\b/i.test(query) ? 'sql' : 'dql'
}

/* ---------------------------------------------------------------------- api */

export interface SearchScope {
  clusterId: string
  scopeId: string
}

/**
 * An absolute time window, sent as `start_time`/`end_time` invoke-body fields
 * (`YYYY-MM-DDTHH:mm:ss`, local zone — the zone the body's query_timezone
 * names). The legacy console sends custom ranges exactly this way, in place
 * of a duration; used only when the query text carries no duration stage.
 */
export interface SearchWindow {
  start: string
  end: string
}

export interface SearchApi {
  /** Events the query would scan. Advisory: never block a search on this. */
  cost: (q: SearchScope & { query: string; dialect?: Dialect; window?: SearchWindow }) => Promise<number | null>
  /** Throws SearchQueryError when the engine rejects the query (DQL path only — SQL failures surface in the result, see SearchResultPage.failed). */
  invoke: (q: SearchScope & { query: string; dialect?: Dialect; window?: SearchWindow }) => Promise<string>
  state: (q: SearchScope & { taskId: string }) => Promise<TaskState>
  result: (
    q: SearchScope & { taskId: string; pageNo?: number; pageSize?: number },
  ) => Promise<SearchResultPage>
  revoke: (q: SearchScope & { taskId: string }) => Promise<void>
  /**
   * The whole query vocabulary in one call: stream name -> its field list.
   * The map keys double as the stream picker. Fields arrive `$`-prefixed
   * (`$SrcIP`) but queries use them bare — see bareField.
   */
  fields: (q: SearchScope) => Promise<Record<string, string[]>>
}

/** The browser's zone, which is what the legacy console sends on every job. */
function localTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

export function createSearchApi(client: ApiClient): SearchApi {
  return {
    cost: async ({ clusterId, scopeId, query, dialect = 'dql', window }) => {
      // Note the missing job_execution — realtimecost does not take one.
      const res = await client.request('POST', `/${clusterId}/wrk/api/job/realtimecost`, {
        body: {
          scope_id: scopeId,
          job_type: dialect,
          query,
          query_timezone: localTimezone(),
          ...(window ? { start_time: window.start, end_time: window.end } : {}),
        },
        schema: costResponse,
      })
      return res.status === 'success' ? res.evt_len : null
    },

    invoke: async ({ clusterId, scopeId, query, dialect = 'dql', window }) => {
      const res = await client.request('POST', `/${clusterId}/wrk/api/job/invoke`, {
        body: {
          scope_id: scopeId,
          job_type: dialect,
          job_execution: 'on-demand',
          query,
          query_timezone: localTimezone(),
          ...(window ? { start_time: window.start, end_time: window.end } : {}),
        },
        schema: invokeResponse,
      })

      if (res.status !== 'success') {
        const msg =
          res.message ?? res.parser_error_info?.error_msg ?? 'The engine rejected that query.'
        throw new SearchQueryError(msg, res.parser_error_info?.parsed_query ?? null, res)
      }

      const taskId = res.data[0]?.id
      if (!taskId) {
        throw new SearchQueryError('The engine accepted the query but started no task.', null, res)
      }
      return taskId
    },

    state: ({ clusterId, taskId }) =>
      client.request('GET', `/${clusterId}/wrk/api/dispatcher/task/state/${taskId}`, {
        schema: taskStateResponse,
      }),

    result: async ({ clusterId, taskId, pageNo = 1, pageSize = 50 }) => {
      const res = await client.request(
        'GET',
        `/${clusterId}/wrk/api/dispatcher/task/result/${taskId}?pagesize=${pageSize}&pageno=${pageNo}`,
        { schema: searchResultResponse },
      )
      // "Task Execution Failed" is the live wording; matched loosely so a
      // wording tweak degrades to a false negative (an empty table), never to
      // a false positive on a healthy result.
      const failed = /fail/i.test(res.task_status ?? '')
      return {
        rows: res.result,
        fields: res.fields_list,
        total: res.total_count,
        executionTime: res.execution_time,
        sourceStreams: res.source_stream,
        failed,
        message: failed ? (res.message || res.task_status) : null,
      }
    },

    revoke: async ({ clusterId, scopeId, taskId }) => {
      await client.request('POST', `/${clusterId}/wrk/api/job/revoke`, {
        body: { task_id: taskId, scope_id: scopeId },
      })
    },

    fields: async ({ clusterId, scopeId }) => {
      const res = await client.request('POST', `/${clusterId}/wrk/api/vfields`, {
        body: { scope_id: scopeId },
        schema: vfieldsResponse,
      })
      return res.result
    },
  }
}

/* ------------------------------------------------------------------ helpers */

/** States that mean "stop polling". Anything not here is still running. */
const RUNNING = new Set(['PENDING', 'STARTED', 'RETRY', 'RECEIVED'])

/**
 * Poll while this is false. The legacy rule is the safe one: only PENDING and
 * STARTED continue, everything else — including states we have never seen —
 * stops. Treating an unknown state as running is how a poll loop runs forever.
 */
export function isTerminal(state: string | null | undefined): boolean {
  if (!state) return false
  return !RUNNING.has(state.toUpperCase())
}

/** A terminal state that did not produce a result. */
export function isFailedState(state: string | null | undefined): boolean {
  if (!state) return false
  const s = state.toUpperCase()
  return s === 'FAILURE' || s === 'REVOKED'
}

/**
 * The five columns every stream leads with, pinned at the left edge in this
 * order so that horizontal scrolling through the other ~30 never hides which
 * event a row is. The sequence is by investigative priority — WHEN, then
 * WHAT KIND (stream, source type), then WHERE FROM — not the server's order.
 * Matched case-insensitively because the names are opaque: SIGNALS returns
 * `cnamtime`, FIREWALL returns `CNAMTime`.
 */
export const IDENTITY_FIELDS = ['CNAMTime', 'Stream', 'SourceType', 'SourceName', 'System']

/**
 * The columns worth rendering: the ones that actually carry a value.
 *
 * This is not a nicety. `fields_list` is the stream's whole vocabulary, and for
 * any real result set roughly 90% of it is null — 294 fields of which 33 were
 * populated on FIREWALL, 783 of which 45 on `stream=*`. Rendering `fields_list`
 * as-is produces a table nobody can read.
 *
 * Computed ONCE per task id by the caller, from the first page fetched, and
 * held for that result set — recomputing per page would reflow the table as the
 * user pages through, which reads as a bug even though it is honest to the data.
 */
export function populatedFields(fields: string[], rows: SearchRow[]): string[] {
  const lower = new Map(fields.map((f) => [f.toLowerCase(), f]))
  const identity = IDENTITY_FIELDS.map((f) => lower.get(f.toLowerCase())).filter(
    (f): f is string => f !== undefined,
  )
  const identitySet = new Set(identity)

  const populated = fields.filter(
    (f) =>
      !identitySet.has(f) &&
      rows.some((r) => {
        const v = r[f]
        return v !== null && v !== undefined && v !== ''
      }),
  )
  return [...identity, ...populated]
}

/**
 * Look a field up by name, tolerating the per-stream spelling.
 *
 * Field names are opaque and their casing varies by stream, so a caller that
 * wants "the Stream column" cannot index by a literal. It also cannot assume
 * the column EXISTS: the SIGNALS stream returns no `System`, `SourceName` or
 * `Stream` at all, so the identity block there is two columns wide, not five.
 * Positional access into the column list is therefore always wrong.
 */
export function fieldNamed(fields: string[], name: string): string | null {
  const want = name.toLowerCase()
  return fields.find((f) => f.toLowerCase() === want) ?? null
}

/**
 * The raw-log column. Its value is the original event line and routinely runs
 * to kilobytes — rendered inline it dominates the table's layout cost and
 * width, so the UI shows it behind a control instead of as text. Matched on
 * the bare name because SQL results prefix fields with `$`.
 */
export function isLogEventField(field: string): boolean {
  return bareField(field).toLowerCase() === 'logevent'
}

/** What a raw log line turned out to contain. */
export type ParsedLogEvent =
  | { kind: 'json'; pretty: string }
  | { kind: 'kv'; pairs: Array<[string, string]> }
  | { kind: 'text' }

/**
 * Make a raw event readable: most device logs are `key=value` grammars
 * (FortiGate-style, values optionally double-quoted) or JSON documents.
 * Parsing is presentation-only — nothing downstream trusts it — so the rules
 * are deliberately loose, and anything that does not parse CONFIDENTLY
 * (at least three k=v pairs) stays plain text rather than shown as a wrong
 * table.
 */
export function parseLogEvent(text: string): ParsedLogEvent {
  const trimmed = text.trim()
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      return { kind: 'json', pretty: JSON.stringify(JSON.parse(trimmed), null, 2) }
    } catch {
      // Looked like JSON, is not; fall through to the k=v attempt.
    }
  }
  const pairs: Array<[string, string]> = []
  for (const m of trimmed.matchAll(/([A-Za-z0-9_.-]+)=(?:"([^"]*)"|(\S+))/g)) {
    pairs.push([m[1]!, m[2] ?? m[3] ?? ''])
  }
  if (pairs.length >= 3) return { kind: 'kv', pairs }
  return { kind: 'text' }
}

/**
 * The vfields vocabulary prefixes every field with `$` (`$SrcIP`); queries use
 * them bare, and result columns come back bare on DQL. One place strips it.
 */
export function bareField(f: string): string {
  return f.startsWith('$') ? f.slice(1) : f
}

/**
 * Timestamps are epoch MILLISECONDS as plain numbers, and nothing in the
 * payload marks them as such. Detection is deliberately doubled up — the field
 * name must look temporal AND the value must sit in a plausible ms range —
 * because `occurrences: 646751` is also a bare number, and a name-only rule
 * would render it as 1970.
 *
 * The name test is a case-insensitive suffix, never an exact key list: the same
 * field is `cnamtime` on one stream and `CNAMTime` on another.
 */
function looksLikeTimestamp(field: string, value: unknown): value is number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return false
  const f = field.toLowerCase()
  if (!(f.endsWith('time') || f.endsWith('tstamp') || f.endsWith('seen'))) return false
  // 1973-01-01 .. 5138 in ms. Seconds-epoch values fall below this and are left
  // as numbers rather than silently rendered as 1970.
  return value >= 1e11 && value < 1e14
}

/**
 * `YYYY-MM-DD HH:MM:SS` in the viewer's LOCAL zone, not UTC.
 *
 * Local is the correct frame here: `invoke` sends the browser's own
 * `query_timezone`, so `duration 1d` already means the last day as this user
 * reckons it. Rendering the answer in UTC would put the result in a different
 * frame from the question that produced it.
 *
 * Hand-built rather than toLocaleString(): the ops console wants one fixed,
 * sortable, locale-independent format, and toISOString() is UTC-only.
 */
function formatTimestamp(ms: number): string {
  const d = new Date(ms)
  const p = (n: number, w = 2) => String(n).padStart(w, '0')
  return (
    `${p(d.getFullYear(), 4)}-${p(d.getMonth() + 1)}-${p(d.getDate())} ` +
    `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
  )
}

/**
 * One cell, as text.
 *
 * Handles what the capture actually contains: nulls, booleans (which must read
 * as false, not as blank — a blank cell says "no data" and that is a different
 * claim), epoch-ms numbers, and nested arrays which arrive JSON-ENCODED INTO A
 * STRING: `"[\"EP-PROCESS\",\"WIN-AUDIT\"]"`.
 */
export function formatCell(field: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (looksLikeTimestamp(field, value)) return formatTimestamp(value)
  if (typeof value === 'number') return String(value)
  if (typeof value === 'string') {
    if (value.startsWith('[') || value.startsWith('{')) {
      try {
        const parsed: unknown = JSON.parse(value)
        if (Array.isArray(parsed)) return parsed.join(', ')
      } catch {
        // Not JSON after all; fall through and show the raw string.
      }
    }
    return value
  }
  return JSON.stringify(value)
}

/**
 * formatCell for a value that may have been STRINGIFIED on its way through
 * the UI — facet keys, chart categories, split-series names are Map keys and
 * therefore strings. A bare 11-14 digit string is re-read as the epoch-ms
 * number it was, so temporal fields render as timestamps, not raw epochs;
 * everything else passes straight to formatCell.
 */
export function displayValue(field: string, value: unknown): string {
  if (typeof value === 'string' && /^\d{11,14}$/.test(value)) {
    return formatCell(field, Number(value))
  }
  return formatCell(field, value)
}

/** Rough count for the cost preview, e.g. 8580 -> '8,580'. */
export function formatEvents(n: number | null): string {
  return n === null ? '—' : n.toLocaleString('en-US')
}

/**
 * Short form for a chip: `760,235` stays as is, `26,396,844` becomes `26.4M`,
 * a billion becomes `1.2B`. Anything under a million is short enough already
 * and exact digits are worth more than a letter. Pair with formatEvents in a
 * title so the exact number is one hover away.
 */
export function compactEvents(n: number | null): string {
  if (n === null) return '—'
  if (n < 1_000_000) return n.toLocaleString('en-US')
  const units: Array<[number, string]> = [
    [1_000_000_000_000, 'T'],
    [1_000_000_000, 'B'],
    [1_000_000, 'M'],
  ]
  for (const [div, suffix] of units) {
    if (n >= div) {
      const v = n / div
      return `${v >= 100 ? Math.round(v).toString() : v.toFixed(1).replace(/\.0$/, '')}${suffix}`
    }
  }
  return n.toLocaleString('en-US')
}
